import React from "react";
import { useTranslation } from "react-i18next";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { displayUrl } from "../../utils/commonUtil";
import apiService from "../../api/apiService";
import SwalAlert from "../common/alert";
import i18n from "../i18n/lang";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "./style.css";

const DOCUMENT_PREVIEW_EVENT = "app:document-preview:open";
const DOCUMENT_PREVIEW_CLOSE_EVENT = "app:document-preview:close";

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|bmp|avif|svg)(\?.*)?$/i;
const PDF_EXTENSION = /\.pdf(\?.*)?$/i;

const isAbsoluteUrl = (value = "") =>
  /^(https?:|blob:|data:|file:)/i.test(String(value));

const resolveUrl = (value) => {
  if (!value) return "";
  if (isAbsoluteUrl(value)) return value;
  return displayUrl(value);
};

const fileNameFromUrl = (value = "") => {
  try {
    const clean = String(value).split("?")[0];
    const name = clean.split("/").filter(Boolean).pop();
    return decodeURIComponent(name || i18n.t("documentPreview.document"));
  } catch (error) {
    return i18n.t("documentPreview.document");
  }
};

const normalizeItem = (source, fallbackTitle = "") => {
  if (!source) return null;

  if (source instanceof Blob) {
    const objectUrl = URL.createObjectURL(source);
    return {
      rawUrl: objectUrl,
      url: objectUrl,
      name: source.name || fallbackTitle || i18n.t("documentPreview.document"),
      mimeType: source.type || "",
      revokeOnClose: true,
    };
  }

  if (typeof source === "object") {
    // Support the file shapes already used around this project/API.
    const nestedFile = source.file instanceof Blob ? source.file : null;
    if (nestedFile) return normalizeItem(nestedFile, source.name || fallbackTitle);

    const rawUrl =
      source.url ||
      source.src ||
      source.path ||
      source.filePath ||
      source.fileUrl ||
      source.downloadUrl ||
      source.documentPath ||
      (typeof source.file === "string" ? source.file : "") ||
      "";
    if (!rawUrl) return null;

    return {
      rawUrl,
      url: resolveUrl(rawUrl),
      name:
        source.name ||
        source.fileName ||
        source.originalName ||
        fallbackTitle ||
        fileNameFromUrl(rawUrl),
      mimeType: source.mimeType || source.type || source.contentType || "",
      revokeOnClose: false,
    };
  }

  const rawUrl = String(source);
  return {
    rawUrl,
    url: resolveUrl(rawUrl),
    name: fallbackTitle || fileNameFromUrl(rawUrl),
    mimeType: "",
    revokeOnClose: false,
  };
};

/**
 * Convert the different document payloads used in the project into one flat list.
 *
 * Supported examples:
 * - "/uploads/a.pdf"
 * - ["/uploads/a.pdf", "/uploads/b.jpg"]
 * - JSON.stringify([...]) / JSON.stringify({ filePath: "..." })
 * - { files: [...] }
 * - { documents: [...] }
 * - { documentPath: "..." }
 * - File / Blob
 */
export const normalizeDocumentSources = (value) => {
  if (!value) return [];

  if (value instanceof Blob) return [value];

  if (Array.isArray(value)) {
    return value.flatMap((item) => normalizeDocumentSources(item));
  }

  if (typeof value === "string") {
    const text = value.trim();
    if (!text || text === "null" || text === "undefined") return [];

    if (text.startsWith("[") || text.startsWith("{")) {
      try {
        return normalizeDocumentSources(JSON.parse(text));
      } catch (error) {
        // It is a normal URL/path that happens to start with a bracket.
      }
    }

    return [text];
  }

  if (typeof value === "object") {
    if (Array.isArray(value.files)) {
      return normalizeDocumentSources(value.files);
    }
    if (Array.isArray(value.documents)) {
      return normalizeDocumentSources(value.documents);
    }
    if (Array.isArray(value.attachments)) {
      return normalizeDocumentSources(value.attachments);
    }

    // Some APIs wrap the real document under one of these properties.
    const hasDirectUrl =
      value.url ||
      value.src ||
      value.path ||
      value.filePath ||
      value.fileUrl ||
      value.downloadUrl ||
      value.file instanceof Blob ||
      typeof value.file === "string";

    if (!hasDirectUrl && value.documentPath) {
      return normalizeDocumentSources(value.documentPath);
    }

    return [value];
  }

  return [];
};

export const getDocumentCount = (sources) =>
  normalizeDocumentSources(sources)
    .map((source) => normalizeItem(source))
    .filter(Boolean).length;

export const openDocumentPreview = (sources, options = {}) => {
  if (typeof window === "undefined") return;

  const items = normalizeDocumentSources(sources)
    .map((source) => normalizeItem(source, options.name || ""))
    .filter(Boolean);

  if (!items.length) return;

  window.dispatchEvent(
    new CustomEvent(DOCUMENT_PREVIEW_EVENT, {
      detail: {
        items,
        title: options.title || i18n.t("documentPreview.title"),
        initialIndex: Number(options.initialIndex || 0),
      },
    })
  );
};

/**
 * Reusable UI trigger for list/table pages.
 *
 * variant:
 * - button: normal compact button
 * - icon: icon-only action button
 * - cell: table-friendly document cell with file count
 */
export const DocumentPreviewUI = ({
  documents,
  sources,
  title,
  label,
  emptyText = "-",
  variant = "button",
  icon = "fas fa-eye",
  className = "",
  showCount = true,
  disabled = false,
  stopPropagation = true,
  initialIndex = 0,
  onClick,
  ...buttonProps
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title || t("documentPreview.title");
  const resolvedLabel = label || t("documentPreview.document");
  const input = documents !== undefined ? documents : sources;
  const count = getDocumentCount(input);
  const isDisabled = disabled || count === 0;

  const handleClick = (event) => {
    if (stopPropagation) event?.stopPropagation?.();
    onClick?.(event);
    if (event?.defaultPrevented || isDisabled) return;

    openDocumentPreview(input, { title: resolvedTitle, initialIndex });
  };

  if (variant === "cell" && count === 0) {
    return <span className={`document-preview-ui__empty ${className}`}>{emptyText}</span>;
  }

  if (variant === "icon") {
    return (
      <button
        type="button"
        className={`document-preview-ui document-preview-ui--icon ${className}`.trim()}
        onClick={handleClick}
        disabled={isDisabled}
        title={count > 0 ? `${resolvedLabel}${showCount ? ` (${count})` : ""}` : emptyText}
        aria-label={resolvedLabel}
        {...buttonProps}
      >
        <i className={icon} />
        {showCount && count > 1 && (
          <span className="document-preview-ui__badge">{count}</span>
        )}
      </button>
    );
  }

  if (variant === "cell") {
    return (
      <button
        type="button"
        className={`document-preview-ui document-preview-ui--cell ${className}`.trim()}
        onClick={handleClick}
        disabled={isDisabled}
        {...buttonProps}
      >
        <span className="document-preview-ui__file-icon">
          <i className="fas fa-file-alt" />
        </span>
        <span className="document-preview-ui__content">
          <span className="document-preview-ui__label">{resolvedLabel}</span>
          {showCount && (
            <span className="document-preview-ui__meta">{t("documentPreview.files", { count })}</span>
          )}
        </span>
        <i className="fas fa-eye document-preview-ui__eye" />
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`document-preview-ui document-preview-ui--button ${className}`.trim()}
      onClick={handleClick}
      disabled={isDisabled}
      {...buttonProps}
    >
      <i className={icon} />
      <span>{resolvedLabel}</span>
      {showCount && count > 0 && (
        <span className="document-preview-ui__count">{count}</span>
      )}
    </button>
  );
};

// Friendly aliases for older/newer calling styles.
export const DocumentPreviewButton = DocumentPreviewUI;
export const DocumentPreviewTrigger = DocumentPreviewUI;

export const closeDocumentPreview = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(DOCUMENT_PREVIEW_CLOSE_EVENT));
};

const getKind = (item) => {
  const url = item?.rawUrl || item?.url || "";
  const mime = String(item?.mimeType || "").toLowerCase();

  if (mime.includes("pdf") || PDF_EXTENSION.test(url)) return "pdf";
  if (mime.startsWith("image/") || IMAGE_EXTENSIONS.test(url) || /^data:image\//i.test(url)) {
    return "image";
  }

  return "other";
};

/**
 * Global document preview intentionally uses a class component.
 *
 * This component lives for the whole application lifetime and is often edited while the
 * development server is running. React Fast Refresh can preserve hook state from an older
 * version of a component. If an older version used a different useEffect dependency shape,
 * React can crash in areHookInputsEqual with `prevDeps.length`.
 *
 * Keeping the global host hook-free avoids that Fast Refresh failure completely.
 */
class GlobalDocumentPreview extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      preview: null,
      activeIndex: 0,
      isDownloading: false,
    };

    this.previousBodyOverflow = "";
  }

  componentDidMount() {
    window.addEventListener(DOCUMENT_PREVIEW_EVENT, this.handleOpen);
    window.addEventListener(DOCUMENT_PREVIEW_CLOSE_EVENT, this.handleClose);
    window.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener(DOCUMENT_PREVIEW_EVENT, this.handleOpen);
    window.removeEventListener(DOCUMENT_PREVIEW_CLOSE_EVENT, this.handleClose);
    window.removeEventListener("keydown", this.handleKeyDown);

    this.revokePreviewUrls(this.state.preview);
    this.restoreBodyScroll();
  }

  revokePreviewUrls = (preview) => {
    preview?.items?.forEach((item) => {
      if (item?.revokeOnClose && item?.url) {
        try {
          URL.revokeObjectURL(item.url);
        } catch (error) {
          // Ignore invalid/already-revoked object URLs.
        }
      }
    });
  };

  lockBodyScroll = () => {
    if (typeof document === "undefined") return;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  };

  restoreBodyScroll = () => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = this.previousBodyOverflow || "";
    this.previousBodyOverflow = "";
  };

  handleOpen = (event) => {
    const detail = event?.detail;
    if (!detail?.items?.length) return;

    const maxIndex = detail.items.length - 1;
    const requestedIndex = Number(detail.initialIndex || 0);
    const activeIndex = Math.min(Math.max(requestedIndex, 0), maxIndex);

    this.setState((current) => {
      this.revokePreviewUrls(current.preview);
      return {
        preview: detail,
        activeIndex,
      };
    });

    this.lockBodyScroll();
  };

  handleClose = () => {
    this.setState((current) => {
      this.revokePreviewUrls(current.preview);
      return {
        preview: null,
        activeIndex: 0,
      };
    });

    this.restoreBodyScroll();
  };

  handleKeyDown = (event) => {
    const { preview } = this.state;
    if (!preview) return;

    if (event.key === "Escape") {
      this.handleClose();
      return;
    }

    if (preview.items.length <= 1) return;

    if (event.key === "ArrowLeft") {
      this.showPrevious();
    } else if (event.key === "ArrowRight") {
      this.showNext();
    }
  };

  showPrevious = () => {
    this.setState(({ preview, activeIndex }) => {
      if (!preview?.items?.length) return null;
      return {
        activeIndex: activeIndex > 0 ? activeIndex - 1 : preview.items.length - 1,
      };
    });
  };

  showNext = () => {
    this.setState(({ preview, activeIndex }) => {
      if (!preview?.items?.length) return null;
      return {
        activeIndex: activeIndex < preview.items.length - 1 ? activeIndex + 1 : 0,
      };
    });
  };

  /**
   * Force a real browser download instead of navigating/opening the file URL.
   *
   * The native `download` attribute is not reliable for cross-origin files; some
   * browsers will open PDFs/images in a new tab instead. Fetching the file as a Blob
   * first makes the final href a same-origin `blob:` URL, so the browser treats it as
   * a download consistently.
   */
  handleDownload = async () => {
    const { preview, activeIndex, isDownloading } = this.state;
    if (isDownloading || !preview?.items?.length) return;

    const item = preview.items[activeIndex];
    if (!item?.url) return;

    this.setState({ isDownloading: true });

    try {
      let blob;

      // Local Blob/Data URLs do not need the API client.
      if (/^(blob:|data:)/i.test(item.url)) {
        const response = await fetch(item.url);
        blob = await response.blob();
      } else {
        // Use the project's axios instance so auth/signature headers are preserved.
        const response = await apiService.get(item.url, {
          responseType: "blob",
        });

        blob =
          response?.data instanceof Blob
            ? response.data
            : new Blob([response?.data], {
                type: item.mimeType || "application/octet-stream",
              });
      }

      if (!blob || blob.size === 0) {
        throw new Error("Empty download response");
      }

      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        item.name || fileNameFromUrl(item.rawUrl || item.url) || "document";
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      link.remove();

      // Delay revoke slightly so Safari/older browsers have time to start saving.
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1500);
    } catch (error) {
      console.error("Document download failed:", error);
      SwalAlert.error(
        i18n.t("documentPreview.downloadFailed"),
        i18n.t("documentPreview.downloadFailedText")
      );
    } finally {
      this.setState({ isDownloading: false });
    }
  };

  render() {
    const { preview, activeIndex, isDownloading } = this.state;
    if (!preview?.items?.length) return null;

    const item = preview.items[activeIndex];
    if (!item) return null;

    const kind = getKind(item);
    const hasMany = preview.items.length > 1;

    return (
      <div className="document-preview" role="dialog" aria-modal="true">
        <button
          className="document-preview__backdrop"
          type="button"
          onClick={this.handleClose}
          aria-label={i18n.t("accessibility.close")}
        />

        <div className="document-preview__dialog">
          <div className="document-preview__header">
            <div className="document-preview__heading">
              <div className="document-preview__icon">
                <i
                  className={
                    kind === "pdf"
                      ? "fas fa-file-pdf"
                      : kind === "image"
                      ? "fas fa-file-image"
                      : "fas fa-file-alt"
                  }
                />
              </div>
              <div>
                <h5>{preview.title || i18n.t("documentPreview.title")}</h5>
                <span>{item.name || i18n.t("documentPreview.document")}</span>
              </div>
            </div>

            <div className="document-preview__actions">
              {hasMany && (
                <span className="document-preview__counter">
                  {activeIndex + 1} / {preview.items.length}
                </span>
              )}
              <button
                type="button"
                className="document-preview__download"
                onClick={this.handleDownload}
                disabled={isDownloading}
                title={isDownloading ? i18n.t("documentPreview.downloading") : i18n.t("documentPreview.download")}
                aria-label={i18n.t("documentPreview.download")}
                style={
                  isDownloading
                    ? { opacity: 0.65, cursor: "wait" }
                    : undefined
                }
              >
                <i
                  className={
                    isDownloading
                      ? "fas fa-spinner fa-spin"
                      : "fas fa-download"
                  }
                />
              </button>
              <button
                type="button"
                className="document-preview__close"
                onClick={this.handleClose}
                title={i18n.t("documentPreview.close")}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </div>

          <div className="document-preview__body">
            {kind === "image" && (
              <div className="document-preview__image-wrap">
                <img src={item.url} alt={item.name || "document"} />
              </div>
            )}

            {kind === "pdf" && (
              <div className="document-preview__pdf">
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                  <Viewer key={item.url} fileUrl={item.url} />
                </Worker>
              </div>
            )}

            {kind === "other" && (
              <iframe
                className="document-preview__iframe"
                title={item.name || "document"}
                src={item.url}
              />
            )}
          </div>

          {hasMany && (
            <>
              <button
                type="button"
                className="document-preview__nav document-preview__nav--prev"
                onClick={this.showPrevious}
                title={i18n.t("documentPreview.previous")}
              >
                <i className="fas fa-chevron-left" />
              </button>
              <button
                type="button"
                className="document-preview__nav document-preview__nav--next"
                onClick={this.showNext}
                title={i18n.t("documentPreview.next")}
              >
                <i className="fas fa-chevron-right" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default GlobalDocumentPreview;
